<template>
    <!-- Two layouts. Rows (the slide-over, unported reports): one row per thing
         to choose, label on the left and control on the right, stacking on small
         screens. Cards (the On-Demand page, inside ReportCardGrid): a Period card
         and a Points card that join the page's grid, with the run button in the
         page header instead of a footer. -->
    <div :class="{ contents: isCards }">
        <!-- When the report covers. -->
        <div    v-if="hasDateInputs"
                :class="dateGroupClass">
            <h2 v-if="isCards && ! useRange && ! useAnchorPicker"
                :class="cardTitleClass">Period</h2>

            <!-- Single-day reports on the card layout: the shared picker in its
                 one-day mode, choosing the same anchor date the row below does.
                 The report still derives its window from the interval. -->
            <date-range-picker  v-if="useAnchorPicker"
                                v-model="anchorDay"
                                mode="single"
                                :timezone="timezone"
                                disable-future-dates
            ></date-range-picker>

            <template   v-if="useStartEndInterval">
                <div :class="rowClass">
                    <div :class="labelClass">
                        <label  for="dp-input-start_date"
                                class="m-0 block text-sm font-medium text-gray-700">
                            Start Date
                        </label>
                        <p class="m-0 mt-0.5 text-sm text-gray-500">The first day included in the report.</p>
                    </div>
                    <div :class="controlClass">
                        <date-time-picker   id="start_date"
                                            v-model="$data._startDate"
                                            :format="format"
                                            :view-mode="viewMode"
                                            :min-date="minDate"
                                            :max-date="maxDate"
                                            :side-by-side="true"
                                            :timezone="timezone"
                        ></date-time-picker>
                    </div>
                </div>

                <div :class="rowClass">
                    <div :class="labelClass">
                        <label  for="dp-input-end_date"
                                class="m-0 block text-sm font-medium text-gray-700">
                            End Date
                        </label>
                        <p class="m-0 mt-0.5 text-sm text-gray-500">The last day included in the report.</p>
                    </div>
                    <div :class="controlClass">
                        <date-time-picker   id="end_date"
                                            v-model="$data._endDate"
                                            :format="format"
                                            :view-mode="viewMode"
                                            :min-date="minDate"
                                            :max-date="maxDate"
                                            :side-by-side="true"
                                            :timezone="timezone"
                        ></date-time-picker>
                    </div>
                </div>
            </template>

            <!-- Single anchor date (interval mode): the report derives its window
                 from the interval, ending on this day. Only shown for reports that
                 opt in (showAnchorDate); most interval reports keep the anchor
                 hidden at "now". -->
            <div    v-if="showAnchorDate && ! useRange && ! useStartEndInterval && ! useAnchorPicker"
                    :class="rowClass">
                <div :class="labelClass">
                    <label  for="dp-input-month"
                            class="m-0 block text-sm font-medium text-gray-700">
                        Reporting Period
                    </label>
                    <p class="m-0 mt-0.5 text-sm text-gray-500">
                        The report covers the interval ending on this day.
                    </p>
                </div>
                <div :class="controlClass">
                    <date-time-picker   id="month"
                                        v-model="$data._endOfInterval"
                                        :format="format"
                                        :view-mode="viewMode"
                                        :min-date="minDate"
                                        :max-date="maxDate"
                                        :timezone="timezone"
                    ></date-time-picker>
                </div>
            </div>

            <!-- Which half of the month a semi-monthly report covers. The
                 script below keeps _semiMonthHalf in sync with the anchor date
                 both ways, but without this control there is no way to pick a
                 half, so an interval report loses that choice entirely. -->
            <div    v-if="showAnchorDate && ! useRange && interval === 'semi-monthly'"
                    :class="rowClass">
                <div :class="labelClass">
                    <label class="m-0 block text-sm font-medium text-gray-700">
                        Half of Month Reported on
                    </label>
                    <p class="m-0 mt-0.5 text-sm text-gray-500">
                        Semi-monthly reports cover either the 1st or the 2nd half of the month.
                    </p>
                </div>
                <div :class="controlClass">
                    <select2    :options="semiMonthIntervals"
                                v-model:value="$data._semiMonthHalf"
                                :close-on-select="true"
                    ></select2>
                </div>
            </div>

            <!-- One range control for the whole report: the shared
                 DateRangePicker, whose presets are the ones in the design.
                 Reports that still post an interval keep working -- when
                 `useRange` is false the anchor-date control above is used. -->
            <div    v-if="useRange"
                    :class="isCards ? 'flex flex-col gap-2' : 'py-4 first:pt-0'">
                <div    v-if="! isCards"
                        class="mb-2">
                    <label class="m-0 block text-base font-semibold text-gray-900">
                        Reporting Period
                    </label>
                    <p class="m-0 mt-0.5 text-sm text-gray-500">
                        The dates this report will cover.
                    </p>
                </div>

                <date-range-picker  v-model="period"
                                    :timezone="timezone"
                                    default-preset="monthToDate"
                                    disable-future-dates
                ></date-range-picker>

                <p  v-if="rangeError"
                    class="m-0 text-sm text-red-600"
                >
                    {{ rangeError }}
                </p>
            </div>
        </div>

        <!-- What the report covers. -->
        <div    v-if="useMeters"
                :class="pickerGroupClass">
            <h2 v-if="isCards"
                :class="cardTitleClass">{{ pickingZones ? 'Zones' : 'Points' }}</h2>

            <!-- Zone picker (picker-type 'zones'): the Area > Zone tree, the zone
                 sibling of the point tree. -->
            <div    v-if="pickingZones"
                    :class="isCards ? null : 'py-4 first:pt-0'">
                <div    v-if="! isCards"
                        class="mb-2">
                    <label class="m-0 block text-sm font-medium text-gray-700">
                        Zones
                    </label>
                    <p class="m-0 mt-0.5 text-sm text-gray-500">
                        Select the zones to include in this report.
                    </p>
                </div>

                <zone-select-tree   v-if="zoneTree.length > 0"
                                    v-model="$data._selectedZones"
                                    :tree="zoneTree"
                ></zone-select-tree>

                <select2    v-else
                            id="selected_zones[]"
                            :multiple="true"
                            :can-select-all="true"
                            :options="zones"
                            v-model:value="$data._selectedZones"
                            :allow-clear="true"
                ></select2>
            </div>

            <!-- Points get a full-width row of their own: picking many points
                 from a dropdown is painful, so this uses the same Area > Zone >
                 Point tree as the Mass Maintenance picker. -->
            <div    v-else
                    :class="isCards ? null : 'py-4 first:pt-0'">
                <div    v-if="! isCards"
                        class="mb-2">
                    <label class="m-0 block text-base font-semibold text-gray-900">
                        Monitoring Points
                    </label>
                    <p class="m-0 mt-0.5 text-sm text-gray-500">
                        Select the points to include in this report.
                    </p>
                </div>

                <point-select-tree  v-if="meterTree.length > 0"
                                    v-model="$data._selectedMeters"
                                    :tree="meterTree"
                ></point-select-tree>

                <!-- Reports that have not supplied a tree keep the original
                     multi-select, so nothing breaks before they are ported. -->
                <select2    v-else
                            id="selected_meters[]"
                            :multiple="true"
                            :options="meters"
                            v-model:value="$data._selectedMeters"
                            :allow-clear="true"
                ></select2>
            </div>
        </div>

        <!-- The Generate Report action: a "why is this disabled / what happened"
             message on the left, the button on the right. It never changes its
             label -- it always reads "Generate Report" and is simply disabled
             until the form is valid.

             `footerMode` chooses HOW it is presented. 'none' (the default, used by
             every not-yet-ported report) is a plain in-flow row at the end of the
             form. 'page' / 'panel' opt into the shared ActionFooter fixed to the
             screen bottom -- 'page' spans the content area on the inline form,
             'panel' pins to the interval slide-over. -->

        <!-- In-flow footer (footerMode 'none'). -->
        <div    v-if="! isCards && footerMode === 'none'"
                class="mt-5 flex flex-col gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div class="min-w-0 flex-1">
                <run-status-message :error-messages="errorMessages"
                                    :disabled-reason="disabledReason"
                ></run-status-message>
            </div>

            <button type="button"
                    :disabled="! canRunReport"
                    class="relative inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:border-blue-700 focus:outline-none active:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600 sm:w-auto sm:min-w-48"
                    @click="runReport">
                <tabler-icon   v-if="isRunning" name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                Generate Report
            </button>
        </div>

        <!-- Fixed footer (footerMode 'page' / 'panel'). -->
        <action-footer  v-else-if="! isCards"
                        :variant="footerMode"
                        :max-width-class="footerMode === 'panel' ? '' : 'max-w-7xl'">
            <template   #message>
                <run-status-message :error-messages="errorMessages"
                                    :disabled-reason="disabledReason"
                ></run-status-message>
            </template>

            <template   #actions>
                <!-- Full 48px (~8mm) tall tap target on a phone; natural height
                     from sm up. -->
                <button type="button"
                        :disabled="! canRunReport"
                        class="relative inline-flex min-h-[48px] items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:border-blue-700 focus:outline-none active:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600 sm:min-h-0 sm:min-w-48"
                        @click="runReport">
                    <tabler-icon   v-if="isRunning" name="loader" size-class="h-4 w-4 animate-spin mr-3"></tabler-icon>
                    Generate Report
                </button>
            </template>
        </action-footer>
    </div>
</template>

<script>
import axios from "axios"
import {companyMixin} from '@/mixins/companyMixin'
import {permissionMixin} from '@/mixins/permissionMixin'
import {iconNamesMixin} from '@/mixins/IconNamesMixin';

import DateTimePicker from "../Reports/VueDatePicker";
import Select2 from "../elements/select2";
import PointSelectTree from "../elements/PointSelectTree.vue";
import ZoneSelectTree from "../elements/ZoneSelectTree.vue";
import DateRangePicker from "../elements/DateRangePicker.vue";
import ActionFooter from "../elements/ActionFooter.vue";
import RunStatusMessage from "./RunStatusMessage.vue";
import TablerIcon from "../elements/TablerIcon.vue";
import {REPORT_CARD_CLASS, REPORT_CARD_TITLE_CLASS} from "./settings/ReportCard.vue";
import {Intervals} from "./Intervals";
import {DateTime} from "luxon";

export default {
    // The shared report run form. Two orthogonal choices:
    //   - date mode: an explicit start/end range (`use-range`) or a single anchor
    //     date the interval derives its window from (the default).
    //   - what to pick: `picker-type` = 'meters' (Area>Zone>Point tree) or 'zones'
    //     (Area>Zone tree), or none (`use-meters=false`, e.g. a report that scores
    //     everything the user can see).
    name: "IntervalSelection",
    mixins: [companyMixin, permissionMixin, iconNamesMixin],
    components: {DateTimePicker, Select2, PointSelectTree, ZoneSelectTree, DateRangePicker, ActionFooter, RunStatusMessage, TablerIcon},
    // Set by ReportCardGrid on the On-Demand page: the card layout, and the
    // header's Run Report button this form reports its state to.
    inject: {
        formLayout: {from: 'reportFormLayout', default: 'rows'},
        reportRun: {from: 'reportRun', default: null},
        gridHasSettings: {from: 'reportGridHasSettings', default: () => () => false},
    },
    props: {
        meters: {
            type: Object,
            default: [],
        },
        // Area > Zone > Point tree for the point picker. When empty the
        // component falls back to the flat `meters` multi-select.
        meterTree: {
            type: Array,
            default: () => ([]),
        },
        // What the run form lets the user pick: 'meters' (Area>Zone>Point) or
        // 'zones' (Area>Zone). Only consulted when useMeters is true.
        pickerType: {
            type: String,
            default: 'meters',
        },
        // Area > Zone tree for the zone picker (pickerType 'zones').
        zoneTree: {
            type: Array,
            default: () => ([]),
        },
        // Flat zone options, fallback when zoneTree is empty.
        zones: {
            type: Object,
            default: () => ({}),
        },
        selectedZones: {
            type: Array,
            default: () => ([]),
        },
        // Show the single anchor-date picker in interval mode. A report that
        // lets the user pick which day (Room Condition) sets it true; reports
        // that take an explicit range use `useRange` instead.
        showAnchorDate: {
            type: Boolean,
            default: false,
        },
        // Use an explicit start/end range instead of the interval + anchor-date
        // pair. Opt-in per report so unported ones keep their current control.
        useRange: {
            type: Boolean,
            default: false,
        },
        maxSpanDays: {
            type: Number,
            default: 31,
        },
        // Set while the report request is in flight, so the button can show a
        // spinner and block a second submit.
        isRunning: {
            type: Boolean,
            default: false,
        },
        // Outcome of the last submit, shown beside the button.
        ranReport: {
            type: Boolean,
            default: false,
        },
        runningErrors: {
            type: Array,
            default: () => ([]),
        },
        // True when this form has the full card width (a ported report using the
        // settings drawer). Unported reports still render in a one-third column,
        // where a side-by-side label/control row would be crushed, so they keep
        // the simpler stacked layout.
        wide: {
            type: Boolean,
            default: false,
        },
        interval: {
            type: String,
            default: '',
        },
        endOfInterval: {
            type: Date,
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        startDate: {
            type: [Date, String],
        },
        endDate: {
            type: [Date, String],
        },
        selectedMeters: {
            type: Array,
        },
        semiMonthHalf: {
            type: String,
            default: '',
        },
        useStartEndInterval: {
          type: Boolean,
          default: false
        },
        useMeters: {
          type: Boolean,
          default: true
        },
        timezone: {
          type: String,
          default: 'America/New_York'
        },
        // Where the Generate Report footer sits:
        //  'none'  -- a normal in-flow row at the end of the form (default).
        //  'page'  -- fixed to the bottom of the screen across the content area
        //             (right of the app sidebar from lg up), for the inline form.
        //  'panel' -- fixed to the bottom of the slide-over (full width on the
        //             mobile bottom sheet, drawer-width and right-aligned from sm
        //             up), for the group-run interval slide-over.
        footerMode: {
            type: String,
            default: 'none',
            validator: (value) => ['none', 'page', 'panel'].includes(value),
        },
    },
    data() {
        return {
            semiMonthIntervals: {
                first: '1st Half (1 - 15)',
                last: '2nd Half (16 - End of Month)',
            },

            formatDictionary: {
                monthly: 'MMMM YYYY',
                'semi-monthly': 'MMMM YYYY',
                daily: 'MMMM DD, YYYY',
                secondly: 'MMMM DD, YYYY  HH:mm',
            },
            viewModeDictionary: {
                monthly: 'months',
                'semi-monthly': 'months',
                daily: 'days',
                secondly: 'days',
            },

            format: null,
            viewMode: null,

            _selectedMeters: this.selectedMeters,
            _selectedZones: this.selectedZones ?? [],
            _startDate: this.startDate,
            _endDate: this.endDate,

            // "Now" is the company's now, not the browser's, so the defaults
            // below (which half of the month we are in, the month-to-date range)
            // match the company calendar the rest of the control anchors on.
            _semiMonthHalf: DateTime.now().setZone(this.timezone).day <= 15 ? 'first' : 'last',

            // Range mode. Defaults to the current month to date, which is the
            // most common ask and matches the old monthly default. `period` is
            // the picker's own { preset, start, end } value (ISO dates);
            // _rangeStart/_rangeEnd are the same days as Dates, which is what
            // reports post through toReportDate().
            period: {
                preset: 'monthToDate',
                start: DateTime.now().setZone(this.timezone).startOf('month').toISODate(),
                end: DateTime.now().setZone(this.timezone).toISODate(),
            },
            _rangeStart: DateTime.now().setZone(this.timezone).startOf('month').toJSDate(),
            _rangeEnd: DateTime.now().setZone(this.timezone).toJSDate(),
            _endOfInterval: DateTime.now().setZone(this.timezone).toJSDate(),
            // The single-day picker's value; its day is _endOfInterval.
            anchorDay: {
                preset: 'today',
                start: DateTime.now().setZone(this.timezone).toISODate(),
                end: DateTime.now().setZone(this.timezone).toISODate(),
            },
        }
    },
    created() {
        this.format = this.getUpdatedFormat()
        this.viewMode = this.getViewMode()

        if (this.isCards && this.reportRun) {
            this.reportRun.run = () => this.runReport()
        }
    },
    beforeUnmount() {
        if (this.isCards && this.reportRun) {
            this.reportRun.run = null
        }
    },
    mounted() {
        if (this.useRange) {
            this.$emit('update:rangeStart', this.$data._rangeStart)
            this.$emit('update:rangeEnd', this.$data._rangeEnd)
        }
    },
    methods: {
        runReport() {
            this.$emit('run-report')
        },
        fixDateForSemiMonthHalf() {
            // If the interval is not semi-monthly, return
            if (this.interval !== Intervals.SEMI_MONTHLY) {
                return
            }

            if (this.$data._semiMonthHalf === 'first') {
                this.$data._endOfInterval.setDate(1)
            }
            else {
                this.$data._endOfInterval.setDate(16)
            }

            this.$emit('update:endOfInterval', this.$data._endOfInterval)
        },
        updateSemiMonthHalf() {
            this.$data._semiMonthHalf = this.$data._endOfInterval.getDate() <= 15 ? 'first' : 'last'
        },
        getUpdatedFormat() {
            if (_.indexOf(_.keys(this.formatDictionary), this.interval) === -1) {
                return 'MMMM DD, YYYY'
            }
            return this.formatDictionary[this.interval]
        },
        getViewMode() {
            if (_.indexOf(_.keys(this.viewModeDictionary), this.interval) === -1) {
                return 'days'
            }
            return this.viewModeDictionary[this.interval]
        },
    },
    computed: {
        isCards() {
            return this.formLayout === 'cards'
        },
        // The single-day picker stands in for the anchor-date row on cards.
        useAnchorPicker() {
            return this.isCards && this.showAnchorDate && ! this.useRange && ! this.useStartEndInterval
        },
        hasDateInputs() {
            return this.useRange || this.useStartEndInterval || this.showAnchorDate
        },
        cardClass() {
            return REPORT_CARD_CLASS
        },
        cardTitleClass() {
            return [REPORT_CARD_TITLE_CLASS, 'mb-4']
        },
        // The range picker draws its own card, so it needs no second box.
        dateGroupClass() {
            if (! this.isCards) {
                return 'divide-y divide-gray-200'
            }

            return (this.useRange || this.useAnchorPicker) ? 'flex flex-col gap-2' : this.cardClass
        },
        pickerGroupClass() {
            // With no Period card beside it, Points takes the wide right
            // column next to the report's settings, or the full width when
            // there are none.
            if (this.isCards) {
                if (this.hasDateInputs) {
                    return this.cardClass
                }
                return this.gridHasSettings()
                    ? [this.cardClass, 'lg:col-start-2 lg:row-start-1']
                    : [this.cardClass, 'lg:col-span-2']
            }

            return this.hasDateInputs
                ? 'divide-y divide-gray-200 border-t border-gray-200'
                : 'divide-y divide-gray-200'
        },
        // What the header's Run Report button shows, in the card layout.
        runState() {
            return {
                canRun: this.canRunReport,
                // Lets the page centre a lone Period card.
                hasPointsCard: this.useMeters,
                isRunning: this.isRunning,
                disabledReason: this.disabledReason,
                errorMessages: this.errorMessages,
            }
        },
        // The picker caps dates at today but knows nothing of the span cap or
        // the company's first day, so both are checked here. Spans count
        // inclusively, so any single calendar month fits in 31 days.
        rangeError() {
            if (! this.useRange) {
                return null
            }

            const start = DateTime.fromISO(this.period?.start ?? '', {zone: this.timezone})
            const end = DateTime.fromISO(this.period?.end ?? '', {zone: this.timezone})

            if (! start.isValid || ! end.isValid) {
                return null
            }

            const spanDays = Math.round(end.endOf('day').diff(start.startOf('day'), 'days').days)
            if (spanDays > this.maxSpanDays) {
                return `Reports cover at most ${this.maxSpanDays} days. Shorten the range, or run a separate report for each month.`
            }

            const minDate = this.minDate ? DateTime.fromJSDate(new Date(this.minDate), {zone: this.timezone}) : null
            if (minDate?.isValid && start < minDate.startOf('day')) {
                return `There is no data before ${minDate.toFormat('LLLL d, yyyy')}. Choose a later start date.`
            }

            return null
        },
        rowClass() {
            return this.wide
                ? 'flex flex-col gap-2 py-4 first:pt-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6'
                : 'py-4 first:pt-0'
        },
        labelClass() {
            return this.wide ? 'sm:flex-1' : 'mb-2'
        },
        // Half the row on wide layouts: a date range or start/end needs more
        // room than a single short value.
        controlClass() {
            return this.wide ? 'w-full sm:w-1/2 sm:shrink-0' : 'w-full'
        },
        errorMessages() {
            return this.isRunning ? [] : (this.runningErrors ?? [])
        },
        // What the picker is choosing, for the disabled reasons + count checks.
        pickingZones() {
            return this.useMeters && this.pickerType === 'zones'
        },
        needsMeterSelection() {
            return this.useMeters && ! this.pickingZones && this.$data._selectedMeters.length === 0
        },
        needsZoneSelection() {
            return this.pickingZones && (this.$data._selectedZones ?? []).length === 0
        },
        canRunReport() {
            return ! this.isRunning && ! this.needsMeterSelection && ! this.needsZoneSelection && ! this.needsValidRange
        },
        needsValidRange() {
            return this.rangeError !== null
        },
        // Why the button is disabled, shown beside it. Null when it is enabled.
        disabledReason() {
            if (this.isRunning) {
                return 'Running report, please wait'
            }

            if (this.needsValidRange) {
                return 'Choose a valid reporting period to continue'
            }

            if (this.needsMeterSelection) {
                return 'Select at least one point to continue'
            }

            if (this.needsZoneSelection) {
                return 'Select at least one zone to continue'
            }

            return null
        },
    },
    watch: {
        runState: {
            handler(value) {
                if (this.isCards && this.reportRun) {
                    this.reportRun.state = value
                }
            },
            immediate: true,
        },
        // The day picked becomes the anchor date, as midnight in the company
        // timezone, exactly what the anchor-date row produced.
        anchorDay(value) {
            const day = DateTime.fromISO(value?.start ?? '', {zone: this.timezone})

            if (day.isValid) {
                this.$data._endOfInterval = day.toJSDate()
            }
        },
        // The picker emits calendar days; read them as midnight in the company
        // timezone so toReportDate() posts back the same day.
        period(value) {
            const start = DateTime.fromISO(value?.start ?? '', {zone: this.timezone})
            const end = DateTime.fromISO(value?.end ?? '', {zone: this.timezone})

            if (start.isValid && end.isValid) {
                this.$data._rangeStart = start.toJSDate()
                this.$data._rangeEnd = end.toJSDate()
            }
        },
        selectedMeters: {
            handler (val, old) {
                if (val !== old) {
                    this.$data._selectedMeters = val
                }
            },
            deep: true
        },
        _selectedMeters: {
            handler(value, oldValue) {
                let allIndex = value.indexOf('all')
                if (allIndex !== -1) {
                    let newSelectedMeters = Object.keys(this.meters)
                    let metersAllIndex = newSelectedMeters.indexOf("all")
                    newSelectedMeters.splice(metersAllIndex, 1)
                    this.$data._selectedMeters = newSelectedMeters

                    this.$emit('update:selectedMeters', this.$data._selectedMeters)
                }

                this.$emit('update:selectedMeters', this.$data._selectedMeters)
            }, deep: true
        },
        selectedZones: {
            handler(val, old) {
                if (val !== old) {
                    this.$data._selectedZones = val
                }
            },
            deep: true,
        },
        _selectedZones: {
            handler() {
                this.$emit('update:selectedZones', this.$data._selectedZones)
            },
            deep: true,
        },
        startDate (val, old) {
          if (val !== old) {
            this.$data._startDate = val
          }
        },
        _startDate(val, old) {
          if (val !== old) {
            this.$emit('update:startDate', this.$data._startDate)
          }
        },
        endDate (val, old) {
          if (val !== old) {
            this.$data._endDate = val
          }
        },
        _endDate(val, old) {
          if (val !== old) {
            this.$emit('update:endDate', this.$data._endDate)
          }
        },
        _rangeStart(val, old) {
            if (val !== old) {
                this.$emit('update:rangeStart', val)
            }
        },
        _rangeEnd(val, old) {
            if (val !== old) {
                this.$emit('update:rangeEnd', val)
            }
        },
        _semiMonthHalf(val, old) {
            if (val !== old) {
                this.$emit('update:semiMonthHalf', val)
                this.fixDateForSemiMonthHalf()
            }
        },
        endOfInterval (val, old) {
            if (val !== old) {
                this.$data._endOfInterval = val
            }
        },
        _endOfInterval(val, old) {
            if (val !== old) {
                this.$emit('update:endOfInterval', this.$data._endOfInterval)
                this.updateSemiMonthHalf()
            }
        },
        interval() {
            this.format = this.getUpdatedFormat()
            this.viewMode = this.getViewMode()
            this.$emit('update:endOfInterval', this.$data._endOfInterval)
            this.updateSemiMonthHalf()
        },
    },
}
</script>

<style scoped>

</style>